import type { ReactNode } from 'react';

/**
 * The bordered box the four panels of a step share (maquette
 * `editeur-protocole.html`, `.panel` / `.panel-title`).
 *
 * Written once because the four are drawn by three files - the direction and
 * the objectives here, the rules and the exit conditions in their own - and a
 * box spelt three times is a box that ends up with three border widths. The
 * versioned layer settled the same thing with `VersionedDialogSection`, and
 * this title carries the same register: small caps in grey, the qualifier
 * beside it in normal weight.
 */

export interface StepPanelProps {
  /** The panel's own name, in the mockup's lower case (« objectifs »). */
  title: string;
  /** What follows the title in normal weight, or null. */
  sub: ReactNode;
  /**
   * The « l'interviewer » pill of §7.2, or null when the panel has no seat.
   *
   * Named `seat` and not `role`: a JSX attribute called `role` is read as an
   * ARIA role by the a11y lint, whatever component it lands on, and this one is
   * a French noun on a pill.
   */
  seat: string | null;
  className: string;
  children: ReactNode;
}

/** The one class string every panel of the step is drawn with. */
export const STEP_PANEL_BOX = 'rounded-md border-2 border-gray-200 bg-white p-5';

export function StepPanel({ title, sub, seat, className, children }: StepPanelProps) {
  return (
    <section className={`${STEP_PANEL_BOX} ${className}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-caption text-gray-600 uppercase tracking-wide">
          {title}
          {sub === null ? null : (
            <span className="ml-2 font-normal normal-case tracking-normal">{sub}</span>
          )}
        </h3>
        {seat === null ? null : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700 text-micro">
            {seat}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
